import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  defaultIntegrationRequestFormValues,
  integrationRequestFormSchema,
  integrationRequestFormToPayload,
  integrationRequestToFormValues,
  type IntegrationRequest,
  type IntegrationRequestFormSchema,
  type IntegrationRequestPayload,
} from '../../../models/IntegrationRequest';

interface UseIntegrationRequestFormProps {
  commerceId: string;
  initialRequest?: IntegrationRequest;
  onSubmit: (payload: IntegrationRequestPayload) => void;
}

const useIntegrationRequestForm = ({
  commerceId,
  initialRequest,
  onSubmit,
}: UseIntegrationRequestFormProps) => {
  const form = useForm<IntegrationRequestFormSchema>({
    resolver: zodResolver(integrationRequestFormSchema),
    defaultValues: initialRequest
      ? integrationRequestToFormValues(initialRequest)
      : defaultIntegrationRequestFormValues,
    mode: 'onChange',
  });

  const [
    type,
    logisticOperatorId,
    allBranches,
    withWebhook,
    serviceWithOperatorIds,
    webhookUrl,
    webhookStatusIds,
  ] = useWatch({
    control: form.control,
    name: [
      'type',
      'logisticOperatorId',
      'allBranches',
      'withWebhook',
      'serviceWithOperatorIds',
      'webhookUrl',
      'webhookStatusIds',
    ],
  });

  const submit = form.handleSubmit((values) =>
    onSubmit(integrationRequestFormToPayload(commerceId, values))
  );

  return {
    form,
    submit,
    values: {
      type,
      logisticOperatorId,
      serviceWithOperatorIds,
      webhookUrl,
      webhookStatusIds,
      allBranches,
      isApi: type === 'API',
      showsWebhook: type === 'WEBHOOK' || withWebhook,
    },
  };
};

export default useIntegrationRequestForm;
